"use client";

import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Button } from "@/components/ui/button";
import {
  ArrowRight,
  Play,
  Sparkles,
  TrendingUp,
  Car,
  DollarSign,
  BarChart3,
  CheckCircle2,
} from "lucide-react";
import { WHATSAPP_CONSULTOR_GENERAL } from "@/lib/constants";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------
type HeroSectionProps = {
  onOpenDiagnostic?: () => void;
};

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------
const BAR_HEIGHTS = [38, 55, 48, 72, 62, 88, 95, 78, 66, 82] as const;

const VEHICLE_ROWS = [
  { model: "BMW 320i", status: "Disponível", price: "R$ 198k", dot: "bg-emerald-500" },
  { model: "Audi A4", status: "Reservado", price: "R$ 234k", dot: "bg-amber-400" },
  { model: "Mercedes C200", status: "Disponível", price: "R$ 287k", dot: "bg-emerald-500" },
] as const;

// ---------------------------------------------------------------------------
// Animation variants
// ---------------------------------------------------------------------------
const containerVariants = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.15 } },
} as const;

const itemVariants = {
  hidden: { opacity: 0, y: 24 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] },
  },
} as const;

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------
const DashboardMockup = () => (
  <div className="relative w-full">
    {/* Floating card — venda realizada */}
    <motion.div
      initial={{ opacity: 0, x: -24, rotate: -4 }}
      animate={{ opacity: 1, x: 0, rotate: -4 }}
      transition={{ duration: 0.7, delay: 0.8, ease: [0.22, 1, 0.36, 1] }}
      className="absolute -top-6 -left-6 z-20 flex items-center gap-3 bg-card/90 backdrop-blur-xl border border-border/60 rounded-2xl px-4 py-3 shadow-2xl"
    >
      <div className="w-9 h-9 rounded-full bg-primary/20 flex items-center justify-center shrink-0">
        <Car className="w-4 h-4 text-primary" aria-hidden />
      </div>
      <div>
        <p className="text-[10px] text-muted-foreground uppercase tracking-widest font-semibold">
          Venda Fechada
        </p>
        <p className="text-sm font-bold text-foreground leading-none mt-0.5">
          Porsche 911 · R$ 850k
        </p>
      </div>
    </motion.div>

    {/* Floating card — margem */}
    <motion.div
      initial={{ opacity: 0, x: 24, rotate: 3 }}
      animate={{ opacity: 1, x: 0, rotate: 3 }}
      transition={{ duration: 0.7, delay: 1.0, ease: [0.22, 1, 0.36, 1] }}
      className="absolute -bottom-6 -right-6 z-20 flex items-center gap-3 bg-card/90 backdrop-blur-xl border border-border/60 rounded-2xl px-4 py-3 shadow-2xl"
    >
      <div className="w-9 h-9 rounded-full bg-emerald-500/15 flex items-center justify-center shrink-0">
        <TrendingUp className="w-4 h-4 text-emerald-400" aria-hidden />
      </div>
      <div>
        <p className="text-[10px] text-muted-foreground uppercase tracking-widest font-semibold">
          Margem Média
        </p>
        <p className="text-sm font-bold text-foreground leading-none mt-0.5">
          +18.4% este mês
        </p>
      </div>
    </motion.div>

    {/* Main window */}
    <motion.div
      initial={{ opacity: 0, y: 48, rotateX: 12 }}
      animate={{ opacity: 1, y: 0, rotateX: 0 }}
      transition={{ duration: 0.9, delay: 0.5, ease: [0.22, 1, 0.36, 1] }}
      style={{ perspective: 1000 }}
      className="relative z-10 w-full rounded-2xl border border-border/50 bg-card shadow-[0_24px_64px_rgba(0,0,0,0.5)] overflow-hidden"
    >
      {/* Window chrome */}
      <div className="h-9 bg-background/60 border-b border-border/40 flex items-center gap-1.5 px-4">
        <span className="w-2.5 h-2.5 rounded-full bg-red-500/40" />
        <span className="w-2.5 h-2.5 rounded-full bg-yellow-500/40" />
        <span className="w-2.5 h-2.5 rounded-full bg-emerald-500/40" />
        <span className="ml-auto text-[10px] text-muted-foreground/50 font-mono">
          nowey.app/garagem
        </span>
      </div>

      <div className="p-5 flex flex-col gap-4">
        {/* KPI row */}
        <div className="grid grid-cols-3 gap-3">
          {[
            { icon: Car, label: "Estoque", value: "43 veíc." },
            { icon: DollarSign, label: "Receita", value: "R$ 142k" },
            { icon: BarChart3, label: "Margem", value: "+18%" },
          ].map(({ icon: Icon, label, value }) => (
            <div
              key={label}
              className="bg-background/40 border border-border/40 rounded-xl p-3"
            >
              <div className="flex items-center gap-1.5 mb-2">
                <Icon className="w-3.5 h-3.5 text-primary" aria-hidden />
                <span className="text-[10px] text-muted-foreground uppercase tracking-widest">
                  {label}
                </span>
              </div>
              <p className="text-base font-bold text-foreground">{value}</p>
            </div>
          ))}
        </div>

        {/* Bar chart */}
        <div className="bg-background/40 border border-border/40 rounded-xl p-4">
          <p className="text-[10px] text-muted-foreground uppercase tracking-widest mb-3">
            Margem por Veículo — Jul
          </p>
          <div className="flex items-end gap-1.5 h-20">
            {BAR_HEIGHTS.map((h, i) => (
              <motion.div
                key={i}
                initial={{ height: 0 }}
                animate={{ height: `${h}%` }}
                transition={{ delay: 0.9 + i * 0.06, duration: 0.5, ease: "easeOut" }}
                className={`flex-1 rounded-t-sm ${
                  i === 6
                    ? "bg-primary shadow-[0_0_8px_var(--primary)]"
                    : "bg-primary/20"
                }`}
                aria-hidden
              />
            ))}
          </div>
        </div>

        {/* Vehicle list */}
        <div className="bg-background/40 border border-border/40 rounded-xl overflow-hidden">
          <div className="px-4 py-2.5 border-b border-border/40">
            <p className="text-[10px] text-muted-foreground uppercase tracking-widest">
              Veículos Disponíveis
            </p>
          </div>
          <ul>
            {VEHICLE_ROWS.map(({ model, status, price, dot }, i) => (
              <li
                key={model}
                className={`flex items-center justify-between px-4 py-2.5 ${
                  i < VEHICLE_ROWS.length - 1 ? "border-b border-border/30" : ""
                }`}
              >
                <div className="flex items-center gap-2.5">
                  <span className={`w-1.5 h-1.5 rounded-full ${dot}`} aria-hidden />
                  <span className="text-xs font-medium text-foreground">{model}</span>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-[10px] text-muted-foreground">{status}</span>
                  <span className="text-xs font-semibold text-foreground">{price}</span>
                </div>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </motion.div>
  </div>
);

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------
export default function HeroSection({ onOpenDiagnostic }: HeroSectionProps) {
  const sectionRef = useRef<HTMLElement>(null);
  const { scrollYProgress } = useScroll({ target: sectionRef, offset: ["start start", "end start"] });
  const visualY = useTransform(scrollYProgress, [0, 1], [0, -60]);
  const visualOpacity = useTransform(scrollYProgress, [0, 0.6], [1, 0.3]);

  const handleDemoClick = () => {
    if (onOpenDiagnostic) {
      onOpenDiagnostic();
      return;
    }
    if (typeof window !== "undefined") {
      window.open(
        `https://wa.me/${WHATSAPP_CONSULTOR_GENERAL}?text=${encodeURIComponent(
          "Olá! Gostaria de agendar uma demonstração do NoWey CRM."
        )}`,
        "_blank",
        "noopener,noreferrer"
      );
    }
  };

  const handleHowItWorksClick = () => {
    document.getElementById("como-funciona")?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section
      ref={sectionRef}
      id="hero"
      className="relative min-h-screen flex items-center pt-24 pb-20 overflow-hidden bg-background"
      aria-labelledby="hero-headline"
    >
      {/* ── Background decorations ── */}
      <div
        className="absolute inset-0 pointer-events-none"
        aria-hidden
      >
        {/* Gradient overlay top → bottom */}
        <div className="absolute inset-0 bg-linear-to-b from-background via-background/95 to-transparent" />
        {/* Grid dots */}
        <div className="absolute inset-0 bg-[radial-gradient(circle,#80808018_1px,transparent_1px)] bg-size-[32px_32px]" />
        {/* Primary glow blob */}
        <div className="absolute top-[-10%] left-[20%] w-[55%] h-[60%] bg-primary/10 rounded-full blur-[140px]" />
        <div className="absolute bottom-[10%] right-[5%] w-[30%] h-[40%] bg-primary/6 rounded-full blur-[100px]" />
      </div>

      <div className="container max-w-6xl mx-auto px-6 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">

          {/* ── Left: text ── */}
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            className="flex flex-col items-start"
          >
            {/* Badge */}
            <motion.div variants={itemVariants}>
              <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-primary/30 bg-primary/10 text-primary text-xs font-semibold uppercase tracking-widest mb-6">
                <Sparkles className="w-3 h-3" aria-hidden />
                CRM para concessionárias
              </span>
            </motion.div>

            {/* Headline */}
            <motion.h1
              id="hero-headline"
              variants={itemVariants}
              className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter leading-[1.05] mb-5 text-foreground"
            >
              Tudo que sua rede de concessionárias precisa{" "}
              <span className="text-primary">
                — em um só lugar
              </span>
            </motion.h1>

            {/* Subtitle */}
            <motion.p
              variants={itemVariants}
              className="text-base md:text-lg text-muted-foreground leading-relaxed mb-8 max-w-lg"
            >
              Do controle de estoque ao financeiro por veículo, o NoWey centraliza a
              operação das suas lojas com visibilidade total para o gestor.
            </motion.p>

            {/* Buttons */}
            <motion.div
              variants={itemVariants}
              className="flex flex-col sm:flex-row gap-3 mb-8"
            >
              <Button
                type="button"
                onClick={handleDemoClick}
                className="group h-12 px-7 rounded-full bg-primary text-primary-foreground hover:bg-primary/90 font-bold text-sm shadow-[0_0_24px_var(--primary)] hover:shadow-[0_0_36px_var(--primary)] transition-all duration-300 gap-2"
                aria-label="Quero uma demo grátis"
              >
                Quero uma demo grátis
                <ArrowRight
                  className="w-4 h-4 group-hover:translate-x-0.5 transition-transform"
                  aria-hidden
                />
              </Button>

              <Button
                type="button"
                variant="ghost"
                onClick={handleHowItWorksClick}
                className="group h-12 px-6 rounded-full border border-border/60 text-muted-foreground hover:text-foreground hover:bg-muted/40 font-medium text-sm transition-all gap-2"
                aria-label="Ver como funciona"
              >
                <span className="flex items-center justify-center w-5 h-5 rounded-full border border-border/80 group-hover:border-foreground/30 transition-colors">
                  <Play className="w-2.5 h-2.5 fill-current" aria-hidden />
                </span>
                Ver como funciona
              </Button>
            </motion.div>

            {/* Trust line */}
            <motion.ul
              variants={itemVariants}
              className="flex flex-wrap gap-x-5 gap-y-1.5"
              aria-label="Diferenciais"
            >
              {["Sem contrato de fidelidade", "Suporte dedicado", "Setup em 48h"].map(
                (item) => (
                  <li key={item} className="flex items-center gap-1.5 text-xs text-muted-foreground">
                    <CheckCircle2 className="w-3.5 h-3.5 text-primary shrink-0" aria-hidden />
                    {item}
                  </li>
                )
              )}
            </motion.ul>
          </motion.div>

          {/* ── Right: dashboard mockup ── */}
          <motion.div
            style={{ y: visualY, opacity: visualOpacity }}
            initial={{ opacity: 0, x: 48 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.9, delay: 0.3, ease: [0.22, 1, 0.36, 1] }}
            className="relative hidden lg:block"
            aria-hidden
          >
            {/*
              Para usar uma imagem real, substitua <DashboardMockup /> por:
              <Image src="/assets/dashboard-preview.png" alt="Preview do dashboard NoWey"
                     fill className="object-contain" priority />
            */}
            <DashboardMockup />
          </motion.div>
        </div>
      </div>
    </section>
  );
}
